// Thin draggable strip on the right edge of the pinned sidebar. Pointer drag
// updates `sidebarWidth` in settingsStore (clamped + localStorage-persisted).
// Double-click resets to the default width. Sits in the flex layout next to
// the sidebar, so its parent height matches automatically.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SIDEBAR_WIDTH_DEFAULT,
  SIDEBAR_WIDTH_MAX,
  SIDEBAR_WIDTH_MIN,
  useSettingsStore,
} from '../../store/settingsStore';

export function SidebarResizer() {
  const sidebarWidth = useSettingsStore((s) => s.sidebarWidth);
  const setSidebarWidth = useSettingsStore((s) => s.setSidebarWidth);

  const [dragging, setDragging] = useState(false);
  // Live drag state kept in a ref so the move handler stays stable.
  const startRef = useRef<{ x: number; width: number } | null>(null);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      startRef.current = { x: e.clientX, width: sidebarWidth };
      setDragging(true);
    },
    [sidebarWidth],
  );

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: PointerEvent) => {
      const start = startRef.current;
      if (!start) return;
      setSidebarWidth(start.width + (e.clientX - start.x));
    };
    const onUp = () => {
      startRef.current = null;
      setDragging(false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    // Suppress text selection + force the resize cursor everywhere during drag.
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [dragging, setSidebarWidth]);

  // Keyboard a11y — focused handle responds to arrow keys.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 32 : 8;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setSidebarWidth(sidebarWidth - step);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setSidebarWidth(sidebarWidth + step);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setSidebarWidth(SIDEBAR_WIDTH_MIN);
    } else if (e.key === 'End') {
      e.preventDefault();
      setSidebarWidth(SIDEBAR_WIDTH_MAX);
    }
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      aria-valuemin={SIDEBAR_WIDTH_MIN}
      aria-valuemax={SIDEBAR_WIDTH_MAX}
      aria-valuenow={sidebarWidth}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onDoubleClick={() => setSidebarWidth(SIDEBAR_WIDTH_DEFAULT)}
      onKeyDown={onKeyDown}
      // Negative right margin lets a wider hit area sit on top of the chat
      // column without shifting layout — the visible bar is only 1px.
      className={`group relative -mr-1 h-full w-2 shrink-0 cursor-col-resize select-none focus:outline-none ${
        dragging ? 'z-30' : ''
      }`}
    >
      <span
        className={`pointer-events-none absolute inset-y-0 left-0 w-px transition-colors ${
          dragging ? 'bg-accent' : 'bg-transparent group-hover:bg-accent/60 group-focus:bg-accent'
        }`}
      />
    </div>
  );
}
